import React, { useEffect, useMemo, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { format } from 'date-fns';
import { Calendar, Download, Filter, Printer, RefreshCw, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface CompanyItem {
  id: string;
  name: string;
}

interface BranchItem {
  id: string;
  name: string;
  companyId: string;
  company?: { name: string };
}

interface SystemUser {
  id: string;
  name: string;
  email: string;
}

type ChangeValue = string | number | boolean | null;

interface AuditChange {
  field: string;
  before: ChangeValue;
  after: ChangeValue;
}

interface AuditItem {
  id: string;
  timestamp: string;
  action: string;
  resource: string;
  actor: { id: string; name: string; email: string; role: string } | null;
  ipAddress: string | null;
  username: string | null;
  branch: { id: string | null; name: string };
  company: { id: string | null; name: string };
  changes: AuditChange[];
  beforeUnavailable: boolean;
}

interface AuditResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  truncated?: boolean;
  items: AuditItem[];
}

type Period = '7d' | '30d' | '90d' | 'all' | 'custom';

type AuditRequestConfig = AxiosRequestConfig & { preserveSessionOnAuthError?: boolean };

const actionLabels: Record<string, string> = {
  QUOTA_INCREASE: 'Cota aumentada',
  QUOTA_DECREASE: 'Cota reduzida',
  QUOTA_SET: 'Cota definida',
  QUOTA_REMOVED: 'Cota removida',
  PROFILE_RATE_CHANGE: 'Velocidade do perfil alterada',
  PROFILE_CREATE: 'Perfil criado',
  ACTIVE_CLASS_CHANGE: 'Turma ativa alterada',
  HOTSPOT_USER_CREATE: 'Usuário criado',
  HOTSPOT_USER_UPDATE: 'Usuário alterado'
};

const actionBadge: Record<string, string> = {
  QUOTA_INCREASE: 'bg-green-100 text-green-800',
  QUOTA_DECREASE: 'bg-orange-100 text-orange-800',
  QUOTA_SET: 'bg-blue-100 text-blue-800',
  QUOTA_REMOVED: 'bg-red-100 text-red-800',
  PROFILE_RATE_CHANGE: 'bg-purple-100 text-purple-800',
  PROFILE_CREATE: 'bg-purple-100 text-purple-800',
  ACTIVE_CLASS_CHANGE: 'bg-amber-100 text-amber-800',
  HOTSPOT_USER_CREATE: 'bg-slate-100 text-slate-800',
  HOTSPOT_USER_UPDATE: 'bg-slate-100 text-slate-800'
};

const categoryLabels: Record<string, string> = {
  quota: 'Cotas (aumento/redução)',
  profile: 'Perfis de velocidade',
  class: 'Turma ativa do vessel',
  user: 'Outros ajustes de usuário',
  '': 'Todas as alterações'
};

const fieldLabels: Record<string, string> = {
  limitBytesTotal: 'Cota',
  username: 'Usuário',
  profile: 'Perfil',
  turma: 'Turma',
  active: 'Ativo',
  password: 'Senha',
  rateLimit: 'Velocidade',
  activeClass: 'Turma ativa',
  created: 'Criado'
};

const formatBytes = (value: ChangeValue) => {
  if (value === null || value === undefined || value === '' || Number(value) <= 0) return 'sem limite';
  const bytes = Number(value);
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb % 1 === 0 ? 0 : 2)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
};

const formatValue = (field: string, value: ChangeValue) => {
  if (field === 'limitBytesTotal') return formatBytes(value);
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const describeChange = (change: AuditChange, beforeUnavailable: boolean) => {
  const label = fieldLabels[change.field] || change.field;
  if (change.field === 'created') return `Criado: ${formatValue(change.field, change.after)}`;
  if (change.field === 'password') return 'Senha alterada';
  if (change.field === 'activeClass') return `${label}: ${formatValue(change.field, change.after)}`;
  if (change.field === 'profile' && change.before === change.after) return `${label}: ${formatValue(change.field, change.after)}`;
  const before = beforeUnavailable && change.before === null ? '?' : formatValue(change.field, change.before);
  return `${label}: ${before} → ${formatValue(change.field, change.after)}`;
};

const quotaDelta = (item: AuditItem) => {
  const change = item.changes.find((c) => c.field === 'limitBytesTotal');
  if (!change || item.beforeUnavailable) return null;
  const before = Number(change.before) > 0 ? Number(change.before) : 0;
  const after = Number(change.after) > 0 ? Number(change.after) : 0;
  if (!before || !after) return null;
  const diff = after - before;
  return `${diff > 0 ? '+' : '−'}${formatBytes(Math.abs(diff))}`;
};

const periodStart = (period: Period) => {
  const days = { '7d': 7, '30d': 30, '90d': 90 }[period as '7d' | '30d' | '90d'];
  if (!days) return undefined;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
};

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const Audit: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([]);
  const [data, setData] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [printItems, setPrintItems] = useState<AuditItem[] | null>(null);

  const [period, setPeriod] = useState<Period>('30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [actorId, setActorId] = useState('');
  const [category, setCategory] = useState('quota');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const requestConfig: AuditRequestConfig = useMemo(() => ({ preserveSessionOnAuthError: true }), []);

  const filterParams = useMemo(
    () => ({
      startDate: period === 'custom' ? (startDate ? new Date(startDate).toISOString() : undefined) : periodStart(period),
      endDate: period === 'custom' && endDate ? new Date(endDate).toISOString() : undefined,
      companyId: companyId || undefined,
      branchId: branchId || undefined,
      actorId: actorId || undefined,
      category: category || undefined,
      search: search.trim() || undefined
    }),
    [actorId, branchId, category, companyId, endDate, period, search, startDate]
  );

  const load = async () => {
    setRefreshing(true);
    try {
      const response = await api.get('/admin/audit/hotspot', {
        ...requestConfig,
        params: { ...filterParams, page, pageSize: 50 }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Falha ao carregar o histórico de auditoria');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAll = async (): Promise<AuditItem[] | null> => {
    try {
      const response = await api.get('/admin/audit/hotspot', {
        ...requestConfig,
        params: { ...filterParams, all: 'true' }
      });
      if (response.data.truncated) {
        toast('Resultado limitado aos 5000 registros mais recentes. Refine os filtros.', { icon: '⚠️' });
      }
      return response.data.items;
    } catch (error) {
      toast.error('Falha ao buscar os registros');
      return null;
    }
  };

  useEffect(() => {
    api.get('/companies', requestConfig).then((r) => setCompanies(r.data || [])).catch(() => toast.error('Falha ao carregar empresas'));
    api.get('/users', requestConfig).then((r) => setSystemUsers(r.data || [])).catch(() => toast.error('Falha ao carregar usuários'));
  }, [requestConfig]);

  useEffect(() => {
    setBranchId('');
    api
      .get('/branches', { ...requestConfig, params: { companyId: companyId || undefined } })
      .then((r) => setBranches(r.data || []))
      .catch(() => toast.error('Falha ao carregar vessels'));
  }, [companyId, requestConfig]);

  useEffect(() => {
    setPage(1);
  }, [filterParams]);

  useEffect(() => {
    if (period === 'custom' && !startDate) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, page]);

  useEffect(() => {
    if (!printItems) return;
    const handleAfterPrint = () => setPrintItems(null);
    window.addEventListener('afterprint', handleAfterPrint);
    const timer = window.setTimeout(() => window.print(), 100);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printItems]);

  const handlePrint = async () => {
    const items = await fetchAll();
    if (items) setPrintItems(items);
  };

  const handleExportCsv = async () => {
    const items = await fetchAll();
    if (!items) return;
    const headers = ['Data/Hora', 'Empresa', 'Vessel', 'Usuário hotspot', 'Alteração', 'Detalhes', 'Diferença', 'Feito por', 'E-mail', 'IP'];
    const rows = items.map((item) => [
      format(new Date(item.timestamp), 'dd/MM/yyyy HH:mm:ss'),
      item.company.name,
      item.branch.name,
      item.username || '',
      actionLabels[item.action] || item.action,
      item.changes.map((c) => describeChange(c, item.beforeUnavailable)).join(' | '),
      quotaDelta(item) || '',
      item.actor?.name || '',
      item.actor?.email || '',
      item.ipAddress || ''
    ]);
    const csv = '﻿' + [headers, ...rows].map((r) => r.map((v) => csvCell(String(v))).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auditoria-hotspot-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filterSummary = [
    period === 'custom'
      ? `Período: ${startDate ? format(new Date(startDate), 'dd/MM/yyyy HH:mm') : '—'} a ${endDate ? format(new Date(endDate), 'dd/MM/yyyy HH:mm') : 'agora'}`
      : `Período: ${{ '7d': 'últimos 7 dias', '30d': 'últimos 30 dias', '90d': 'últimos 90 dias', all: 'todo o histórico' }[period]}`,
    `Empresa: ${companies.find((c) => c.id === companyId)?.name || 'todas'}`,
    `Vessel: ${branches.find((b) => b.id === branchId)?.name || 'todos'}`,
    `Feito por: ${systemUsers.find((u) => u.id === actorId)?.name || 'todos'}`,
    `Tipo: ${categoryLabels[category]}`,
    search.trim() ? `Usuário hotspot contém: "${search.trim()}"` : null
  ].filter(Boolean);

  const renderRows = (items: AuditItem[]) =>
    items.map((item) => {
      const delta = quotaDelta(item);
      return (
        <tr key={item.id} className="hover:bg-gray-50 audit-row">
          <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
            {format(new Date(item.timestamp), 'dd/MM/yyyy HH:mm:ss')}
          </td>
          <td className="px-4 py-3 text-sm text-gray-700">
            {item.company.name} / {item.branch.name}
          </td>
          <td className="px-4 py-3 text-sm text-gray-800 font-medium">{item.username || '—'}</td>
          <td className="px-4 py-3 text-sm">
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold whitespace-nowrap ${actionBadge[item.action] || 'bg-gray-100 text-gray-700'}`}>
              {actionLabels[item.action] || item.action}
            </span>
          </td>
          <td className="px-4 py-3 text-sm text-gray-700">
            {item.changes.length === 0 ? '—' : (
              <ul className="space-y-0.5">
                {item.changes.map((change, index) => (
                  <li key={index}>{describeChange(change, item.beforeUnavailable)}</li>
                ))}
              </ul>
            )}
            {delta && (
              <span className={`text-xs font-semibold ${delta.startsWith('+') ? 'text-green-700' : 'text-orange-700'}`}>{delta}</span>
            )}
          </td>
          <td className="px-4 py-3 text-sm text-gray-700">
            <div className="font-medium text-gray-800">{item.actor?.name || '—'}</div>
            <div className="text-xs text-gray-500">{item.actor?.email}</div>
          </td>
          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{item.ipAddress || '—'}</td>
        </tr>
      );
    });

  const tableHead = (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa / Vessel</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário hotspot</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alteração</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Antes → Depois</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feito por</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
      </tr>
    </thead>
  );

  if (printItems) {
    return (
      <div className="audit-print">
        <h1 className="text-xl font-semibold text-gray-900">Auditoria de alterações do hotspot</h1>
        <p className="text-xs text-gray-600 mt-1">Gerado em {format(new Date(), 'dd/MM/yyyy HH:mm:ss')} • {printItems.length} registros</p>
        <p className="text-xs text-gray-600 mt-1 mb-4">{filterSummary.join(' • ')}</p>
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          {tableHead}
          <tbody className="divide-y divide-gray-100">{renderRows(printItems)}</tbody>
        </table>
        <button onClick={() => setPrintItems(null)} className="no-print mt-4 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
          Voltar
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const selectClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Auditoria</h1>
          <p className="text-sm text-gray-500">
            Quem alterou cotas, perfis de velocidade e turma ativa, com o valor antes e depois.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => load()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
          >
            <RefreshCw size={16} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          >
            <Download size={16} />
            Exportar CSV
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
          >
            <Printer size={16} />
            Imprimir
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 text-gray-700 mb-4">
          <Filter size={16} />
          <h2 className="text-sm font-semibold uppercase tracking-wide">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Período</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} className={selectClass}>
              <option value="7d">7 dias</option>
              <option value="30d">30 dias</option>
              <option value="90d">90 dias</option>
              <option value="all">Tudo</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Empresa</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={selectClass}>
              <option value="">Todas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Vessel</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={selectClass}>
              <option value="">Todos</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.company?.name ? `${branch.company.name} - ` : ''}{branch.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Feito por</label>
            <select value={actorId} onChange={(e) => setActorId(e.target.value)} className={selectClass}>
              <option value="">Todos</option>
              {systemUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Tipo</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Usuário hotspot</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ex.: joao.silva" className={selectClass} />
          </div>
        </div>

        {period === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Início</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${selectClass} pl-8`} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Fim</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                <input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`${selectClass} pl-8`} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <ShieldCheck size={16} className="text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-800">Histórico de alterações</h3>
          <div className="ml-auto text-xs text-gray-500">{data?.total || 0} registros</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            {tableHead}
            <tbody className="divide-y divide-gray-100">
              {renderRows(data?.items || [])}
              {(data?.items?.length || 0) === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                    Nenhuma alteração registrada para o filtro selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Página {data?.page || 1} de {data?.totalPages || 1} • {data?.total || 0} registros
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={(data?.page || 1) <= 1}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((current) => Math.min(data?.totalPages || current, current + 1))}
              disabled={(data?.page || 1) >= (data?.totalPages || 1)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Audit;
